import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown, ArrowUpRight, Instagram, Youtube, Music } from 'lucide-react';
import { Button } from './ui/button';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [currentText, setCurrentText] = useState(0); // 0 = "Hemen Eğitime Katıl", 1 = "Seni Bekliyorum"
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [isLogoHovered, setIsLogoHovered] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Handle hash links for smooth scrolling
  const handleNavClick = (e, link) => {
    if (link.includes('#')) {
      e.preventDefault();
      const hash = link.split('#')[1];
      
      setIsMobileMenuOpen(false);
      
      const scrollToElement = (targetHash, retries = 25) => {
        const element = document.getElementById(targetHash);
        if (element) {
          const headerOffset = 80;
          const elementPosition = element.getBoundingClientRect().top + window.scrollY;
          window.scrollTo({ top: elementPosition - headerOffset, behavior: 'smooth' });
        } else if (retries > 0) {
          setTimeout(() => scrollToElement(targetHash, retries - 1), 250);
        }
      };

      if (location.pathname === '/') {
        setTimeout(() => scrollToElement(hash), 300);
      } else {
        navigate('/');
        setTimeout(() => scrollToElement(hash), 800);
      }
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Auto-rotate text every 3 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentText(prev => prev === 0 ? 1 : 0);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  // Mouse tracking for 3D parallax effect
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (isLogoHovered) {
        const x = (e.clientX / window.innerWidth - 0.5) * 20; // -10 to 10
        const y = (e.clientY / window.innerHeight - 0.5) * 20; // -10 to 10
        setMousePosition({ x, y });
      }
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, [isLogoHovered]);

  const menuItems = [
    { name: 'Eğitimlerim', hasDropdown: false, link: '/#trainings-section' },
    { name: 'Blog', hasDropdown: false, link: '/blog' },
    { name: 'Hakkımda', hasDropdown: false, link: '/#hakkimda' },
    { name: 'Uzmanlık Alanlarım', hasDropdown: true, link: '/#uzmanlik' },
    { name: 'İletişim', hasDropdown: false, link: '/#iletisim' },
  ];

  const handleCTAClick = () => {
    navigate('/masterclass');
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-[#0a0a15]/95 backdrop-blur-md shadow-lg shadow-purple-500/20' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo - SVG */}
          <div className="flex-shrink-0">
            <a 
              href="/" 
              className="flex items-center group/logo"
            >
              <img 
                src="/tanertorun-beyaz-new.svg" 
                alt="Taner Torun" 
                className="h-8 sm:h-9 md:h-10 w-auto transition-all duration-300 group-hover/logo:scale-105"
              />
            </a>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-8">
            {menuItems.map((item) => (
              <div key={item.name} className="relative group">
                {item.link ? (
                  item.link.includes('#') ? (
                    <a 
                      href={item.link}
                      onClick={(e) => handleNavClick(e, item.link)}
                      className="text-white hover:text-purple-400 transition-colors duration-200 flex items-center gap-1 text-sm font-medium tracking-wide cursor-pointer"
                    >
                      {item.name}
                      {item.hasDropdown && <ChevronDown className="w-4 h-4" />}
                    </a>
                  ) : (
                    <Link 
                      to={item.link} 
                      className="text-white hover:text-purple-400 transition-colors duration-200 flex items-center gap-1 text-sm font-medium tracking-wide"
                    >
                      {item.name}
                      {item.hasDropdown && <ChevronDown className="w-4 h-4" />}
                    </Link>
                  )
                ) : (
                  <button className="text-white hover:text-purple-400 transition-colors duration-200 flex items-center gap-1 text-sm font-medium tracking-wide">
                    {item.name}
                    {item.hasDropdown && <ChevronDown className="w-4 h-4" />}
                  </button>
                )}
              </div>
            ))}
          </nav>

          {/* CTA Button - Auto-rotating Interactive Button */}
          <div className="hidden lg:block">
            <button
              onClick={handleCTAClick}
              className="relative flex items-center gap-3 rounded-full border border-white/20 bg-white/5 backdrop-blur-md px-6 py-3 transition-all duration-500 ease-out hover:border-white/30 hover:bg-white/10 hover:shadow-[0_0_30px_rgba(168,85,247,0.3)]"
            >
              {/* Live pulse indicator - Yeşil/Mor değişim */}
              <div className="relative flex items-center justify-center">
                <span className="relative flex h-2 w-2">
                  <span 
                    className="absolute inline-flex h-full w-full animate-ping rounded-full opacity-75 transition-colors duration-1000"
                    style={{
                      backgroundColor: currentText === 0 ? '#10b981' : '#a855f7'
                    }}
                  />
                  <span 
                    className="relative inline-flex h-2 w-2 rounded-full transition-colors duration-1000"
                    style={{
                      backgroundColor: currentText === 0 ? '#10b981' : '#a855f7'
                    }}
                  />
                </span>
              </div>

              {/* Text with auto flip animation */}
              <div className="flex items-center gap-2 overflow-hidden h-5 relative w-[200px]">
                <span
                  className="text-sm font-light text-white transition-all duration-700 whitespace-nowrap uppercase tracking-wide absolute left-0"
                  style={{
                    transform: currentText === 0 ? 'translateY(0)' : 'translateY(-150%)',
                    opacity: currentText === 0 ? 1 : 0,
                  }}
                >
                  Hemen Eğitime Katıl
                </span>

                <span
                  className="text-sm font-semibold text-white transition-all duration-700 whitespace-nowrap uppercase tracking-wide absolute left-0"
                  style={{
                    transform: currentText === 1 ? 'translateY(0)' : 'translateY(150%)',
                    opacity: currentText === 1 ? 1 : 0,
                  }}
                >
                  SENİ BEKLİYORUM ✨
                </span>
              </div>

              {/* Arrow indicator */}
              <ArrowUpRight
                className="h-4 w-4 text-purple-300 transition-all duration-700"
                style={{
                  transform: currentText === 1 ? 'translateX(2px) translateY(-2px) rotate(0deg)' : 'translateX(0) translateY(0) rotate(-45deg)',
                  opacity: currentText === 1 ? 1 : 0.7,
                }}
              />
            </button>
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden text-white hover:text-purple-400 transition-colors"
            aria-label={isMobileMenuOpen ? "Menüyü kapat" : "Menüyü aç"}
          >
            {isMobileMenuOpen ? <X className="w-7 h-7" /> : <Menu className="w-7 h-7" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="lg:hidden bg-[#0a0a15]/98 backdrop-blur-md border-t border-purple-500/20">
          <div className="px-4 pt-2 pb-6 space-y-4">
            {menuItems.map((item) => (
              item.link.includes('#') ? (
                <a
                  key={item.name}
                  href={item.link}
                  onClick={(e) => handleNavClick(e, item.link)}
                  className="block text-white hover:text-purple-400 transition-colors py-2 text-sm font-medium tracking-wide"
                >
                  {item.name}
                </a>
              ) : (
                <Link
                  key={item.name}
                  to={item.link}
                  className="block text-white hover:text-purple-400 transition-colors py-2 text-sm font-medium tracking-wide"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {item.name}
                </Link>
              )
            ))}
            <Button 
              onClick={() => {
                setIsMobileMenuOpen(false);
                handleCTAClick();
              }}
              className="group relative w-full bg-white/5 backdrop-blur-md border border-white/10 hover:bg-white/10 text-white font-light py-3 rounded-2xl transition-all duration-300 hover:scale-[1.02] shadow-lg hover:shadow-white/10 overflow-hidden hidden lg:block"
            >
              <span className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/5 to-white/0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></span>
              <span className="relative uppercase tracking-wide text-sm">MASTERCLASS'A KATIL</span>
            </Button>
            {/* Social Icons - Mobile Only */}
            <div className="flex items-center justify-center gap-5 pt-2" data-testid="mobile-menu-social">
              <a href="https://www.instagram.com/tanertorun.limitless/" target="_blank" rel="noopener noreferrer" className="text-white/50 hover:text-pink-400 transition-colors" aria-label="Instagram">
                <Instagram className="w-5 h-5" />
              </a>
              <a href="https://www.youtube.com/@zihnef%C4%B1s%C4%B1ldayanadam" target="_blank" rel="noopener noreferrer" className="text-white/50 hover:text-red-400 transition-colors" aria-label="YouTube">
                <Youtube className="w-5 h-5" />
              </a>
              <a href="https://open.spotify.com/show/4CaRzSjI2b9ALAqA4VLjKH?si=FSbRYW5oTriuf9b0WeD7mw" target="_blank" rel="noopener noreferrer" className="text-white/50 hover:text-[#1DB954] transition-colors" aria-label="Spotify">
                <Music className="w-5 h-5" />
              </a>
              <a href="https://www.tiktok.com/@zihnefisildayanadam" target="_blank" rel="noopener noreferrer" className="text-white/50 hover:text-white transition-colors" aria-label="TikTok">
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1v-3.5a6.37 6.37 0 00-.79-.05A6.34 6.34 0 003.15 15.2a6.34 6.34 0 0010.86 4.46V13.2a8.16 8.16 0 005.58 2.17v-3.44a4.85 4.85 0 01-1.99-.43 4.83 4.83 0 01-1.78-1.31V6.69h3.77z"/></svg>
              </a>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;